import * as API from '@/API'

const createState = () => ({
  searchResult: [],
  isLoading: false,
  resource_types: [],
  filter_text: '',
  provisioningstatus: [],
  speed: [],
  type: [],
  preferences: [],
  servicesEnds: {},
})

/**
 * Get resource type from search description field
 * @param {Object} result Result to get resource type from
 * @returns {string} Resource type
 */
const helperResourceType = result => {
  return result.description.split(':')[0]
}

const actions = {
  /**
   * Get search result from API
   * @param {*} {commit} Commit mutation
   * @param {string} searchQuery Query to search for
   * @returns Promise
   */
  getSearchResult: async ({ commit }, searchQuery) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.searchRequest(searchQuery)
      commit('setSearchResult', results)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },
}

const getters = {
  /**
   * Populate list of resource types to filter from
   * @param {Object} state Store state
   * @returns {Object} Object containing list and count of resource types in search result
   */
  resourceTypes(state) {
    const resourceSet = {}
    state.searchResult.forEach(result => {
      const type = helperResourceType(result)
      resourceSet[type] = ++resourceSet[type] || 1
    })
    return resourceSet
  },

  /**
   * Get list of options to filter with
   * @param {Object} state Store state
   * @returns {string[]} Array of items to filter with per category
   */
  serviceOptionList: state => property => {
    const serviceSet = new Set()
    state.searchResult.forEach(result => {
      if (helperResourceType(result) === 'Service' && result.details && result.details[property]) serviceSet.add(result.details[property])
    })
    return Array.from(serviceSet).sort((a, b) => (a > b ? 1 : -1))
  },

  /**
   * Apply active filters to a search result
   * @param {Object} state Store state
   * @param {Object} gettersProp Store getters
   * @returns {Object[]} Filtered array of search results
   */
  filteredResult(state, gettersProp) {
    return state.searchResult
      .filter(result => {
        const { details = {}, ...otherDescription } = result
        return gettersProp.activeFilters.every(option => {
          return gettersProp[option](details, otherDescription)
        })
      })
  },

  /**
   * Filter by resource
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter
   */
  resourceFilter: state => (details, otherDescription) => {
    return state.resource_types.includes(helperResourceType(otherDescription))
  },

  /**
   * Filter by text
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter text
   */
  filterText: state => (details, otherDescription) => {
    const filterMatchedText = (obj, value) => {
      return JSON.stringify(Object.values(obj))
        .toLowerCase()
        .includes(value.toLowerCase())
    }
    return filterMatchedText(details, state.filter_text) || filterMatchedText(otherDescription, state.filter_text)
  },

  /**
   * Filter service by provisioning status
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter
   */
  provisioningFilter: state => (details, otherDescription) => {
    if (helperResourceType(otherDescription) === 'Service') {
      return state.provisioningstatus.includes(details['provisioningstatus'])
    }
    return true
  },

  /**
   * Filter service by speed
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter
   */
  speedFilter: state => (details, otherDescription) => {
    if (helperResourceType(otherDescription) === 'Service') {
      return state.speed.includes(details['speed'])
    }
    return true
  },

  /**
   * Filter service by type
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter
   */
  typeFilter: state => (details, otherDescription) => {
    if (helperResourceType(otherDescription) === 'Service') {
      return state.type.includes(details['type'])
    }
    return true
  },

  /**
   * Filter by preferences
   * @param {Object} state Store state
   * @returns {boolean} Returns true if result contains filter
   */
  preferencesFilter: state => (details, otherDescription) => {
    const isCompanyfilter = state.preferences.filter(value => value.type === 'Company' && value.checked)
    const isPersonfilter = state.preferences.filter(value => value.type === 'Person' && value.checked)
    const isServicefilter = state.preferences.filter(value => value.type === 'Service' && value.checked)

    if (helperResourceType(otherDescription) === 'Company' && isCompanyfilter.length > 0) {
      return isCompanyfilter.every(filter => !!details[filter.value])
    }
    if (helperResourceType(otherDescription) === 'Person' && isPersonfilter.length > 0) {
      return isPersonfilter.every(filter => details[filter.value])
    }
    if (helperResourceType(otherDescription) === 'Service' && isServicefilter.length > 0) {
      return isServicefilter.every(filter => details[filter.value] !== 'DECOMMISSIONED')
    }
    return true
  },

  /**
   * Return unfiltered search results for search table
   * @param {Object} state Store state
   * @returns {Object} Search results
   */
  tableItem(state) {
    return state.searchResult
      .map(result => {
        const { details, ...description } = result
        return {
          details,
          description,
        }
      })
  },

  /**
   * Get active filters
   * @param {Object} state Store state
   * @returns {string[]} Array with names of filters applied
   */
  activeFilters(state) {
    const { resource_types, filter_text, provisioningstatus, speed, type, preferences } = state
    const allFilterGetters = {
      resourceFilter: resource_types,
      filterText: filter_text,
      provisioningFilter: provisioningstatus,
      speedFilter: speed,
      typeFilter: type,
      preferencesFilter: preferences,
    }
    return Object.keys(allFilterGetters).filter(key => (key === 'preferencesFilter' ? preferences.some(value => value.checked) : allFilterGetters[key].length > 0))
  },

  getEndIdByServiceId: state => (serviceId, endName) => {
    if (!Object.hasOwn(state.servicesEnds, serviceId)) return ''
    if (!Object.hasOwn(state.servicesEnds[serviceId], endName)) return ''
    return state.servicesEnds[serviceId][endName]
  },
}

const mutations = {
  /**
   * Save search data to store state
   * @param {Object} state Store state
   * @param {Object[]} data Search data
   */
  setSearchResult: (state, data) => {
    state.searchResult = data
  },

  /**
   * Set search loading status
   * @param {Object} state Store state
   * @param {boolean} value Loading status
   */
  setLoadingStatus: (state, value) => {
    state.isLoading = value
  },

  /**
   * Update state with filter options
   * @param {Object} state Store state
   * @param {Object} value Filter options
   */
  setFilterOptions: (state, value) => {
    state.resource_types = value.resource_types
    state.filter_text = value.filter_text
    state.provisioningstatus = value.provisioningstatus
    state.speed = value.speed
    state.type = value.type
    state.preferences = value.preferences
  },

  addServiceEnds: (state, serviceEnds) => {
    state.servicesEnds = {
      ...state.servicesEnds,
      ...serviceEnds,
    }
  },

  removeServiceEnds: (state, serviceId) => {
    if (Object.prototype.hasOwnProperty.call(state.servicesEnds, serviceId)) {
      delete state.servicesEnds[serviceId]
    }
  },
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state: createState(),
}
